import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useLocalStorage } from "vue-composable";
import { useAuth } from "../../../../store/auth";
import type { Ref } from "vue";

const RECENT_MAX = 3;
export const SIMPLE_THRESHOLD_DEFAULT = 8; // ENG-21322: show search + recents above this count

export function useChangeWorkspace(
  close: () => void,
  dialogRef: Ref<HTMLElement | null>,
  simpleThreshold = SIMPLE_THRESHOLD_DEFAULT,
) {
  const auth = useAuth();
  const { storage: recentTenantIds } = useLocalStorage<string[]>("recent-workspaces", []);

  const searchQuery = ref("");

  onMounted(() => {
    nextTick(() => {
      const el =
        dialogRef.value?.querySelector<HTMLElement>("input") ??
        dialogRef.value?.querySelector<HTMLElement>(".change-workspace-item");
      el?.focus();
    });
  });

  const allTenants = computed(() => {
    const t = auth.tenants ?? {};
    return Object.entries(t).map(([key, data]) => ({ key, name: data.name }));
  });

  const currentKey = computed(() => auth.selectedTenant ?? "");

  // ENG-21322: resolved tenant object, used for display in the "Current" section
  const currentTenant = computed(() => allTenants.value.find((t) => t.key === currentKey.value));

  const isSimple = computed(() => allTenants.value.length <= simpleThreshold);

  // ENG-21322: roving tabindex — exactly one list item is tab-reachable at a
  // time, so there's always a keyboard entry point even in simple mode.
  const activeItemKey = ref("");

  function setActive(key: string) {
    activeItemKey.value = key;
  }

  function itemTabindex(key: string): 0 | -1 {
    return key === activeItemKey.value ? 0 : -1;
  }

  const recent = computed(() => {
    const ids = recentTenantIds.value ?? [];
    const all = allTenants.value;
    return ids
      .filter((id) => id !== currentKey.value)
      .slice(0, RECENT_MAX)
      .flatMap((id) => {
        const tenant = all.find((t) => t.key === id);
        return tenant ? [tenant] : [];
      });
  });

  const allOther = computed(() =>
    allTenants.value.filter(
      (t) => t.key !== currentKey.value && !recent.value.find((r) => r.key === t.key),
    ),
  );

  const filteredTenants = computed(() => {
    if (!searchQuery.value) return [];
    const q = searchQuery.value.toLowerCase();
    return allTenants.value.filter((t) => t.name.toLowerCase().includes(q));
  });

  const showSearchResults = computed(() => searchQuery.value.length > 0);

  // ENG-21322: keep the roving tab stop on a currently-rendered item (only
  // when it goes out of view) so Tab from search never wraps straight to the ✕.
  watch(
    [currentKey, allTenants, filteredTenants, showSearchResults],
    () => {
      const visible = showSearchResults.value ? filteredTenants.value : allTenants.value;
      if (!visible.some((t) => t.key === activeItemKey.value)) {
        activeItemKey.value = showSearchResults.value
          ? filteredTenants.value[0]?.key ?? ""
          : currentKey.value || allTenants.value[0]?.key || "";
      }
    },
    { immediate: true },
  );

  function getHighlightParts(text: string): Array<{ t: string; hi: boolean }> {
    if (!searchQuery.value) return [{ t: text, hi: false }];
    const q = searchQuery.value;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return [{ t: text, hi: false }];
    return [
      { t: text.slice(0, idx), hi: false },
      { t: text.slice(idx, idx + q.length), hi: true },
      { t: text.slice(idx + q.length), hi: false },
    ].filter((p) => p.t.length > 0);
  }

  function switchTo(key: string) {
    if (key !== currentKey.value) {
      const ids = [key, ...(recentTenantIds.value ?? []).filter((id) => id !== key)].slice(
        0,
        RECENT_MAX + 1,
      );
      recentTenantIds.value = ids;
      // ENG-21384: hard-navigate to the new workspace's home. An SPA push flips
      // auth.selectedTenant while the current record page is still mounted, firing a
      // cross-tenant fetch (e.g. shared-care with the old patient id →
      // PatientNotFoundException). A full load tears the app down first, so we always
      // land cleanly on the new workspace's homepage.
      window.location.assign(`/${key}`);
      return;
    }
    close();
  }

  function listItems(): HTMLElement[] {
    return Array.from(
      dialogRef.value?.querySelectorAll<HTMLElement>(".change-workspace-item") ?? [],
    );
  }

  function onSearchKeydown(e: KeyboardEvent) {
    // ENG-21322: only ArrowDown here — intercepting Tab would fight the modal's
    // focus trap (the roving tab-stop is also its "last" focusable).
    if (e.key === "ArrowDown") {
      const items = listItems();
      if (!items.length) return;
      e.preventDefault();
      items[0].focus();
    }
  }

  function onItemKeydown(e: KeyboardEvent) {
    const items = listItems();
    const idx = items.indexOf(e.currentTarget as HTMLElement);
    if (idx === -1) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (idx === 0) dialogRef.value?.querySelector<HTMLElement>("input")?.focus();
      else items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Tab" && e.shiftKey && idx === 0) {
      e.preventDefault();
      dialogRef.value?.querySelector<HTMLElement>("input")?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  }

  return {
    auth,
    searchQuery,
    currentKey,
    currentTenant,
    isSimple,
    allTenants,
    recent,
    allOther,
    filteredTenants,
    showSearchResults,
    getHighlightParts,
    itemTabindex,
    setActive,
    close,
    switchTo,
    onSearchKeydown,
    onItemKeydown,
  };
}
